/* eslint-disable no-use-before-define */
import React, {useState} from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {SEARCH_DEBOUNCE_DELAY} from '@shared/constants/Mapping';
import {useDebouncedCallback} from 'use-debounce';
import {Checkbox, CircularProgress, makeStyles} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import {AutocompleteProps} from '@material-ui/lab/Autocomplete';
import {INPUT_BORDER_COLOR, INPUT_LABEL_COLOR} from 'src/style/variable';
type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;
type WithOptional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;
interface IMultipleAutocompleteInputProps<T>
  extends Omit<
    WithOptional<
      AutocompleteProps<T, undefined, undefined, undefined>,
      'options' | 'renderInput'
    >,
    'onChange' | 'value'
  > {
  fetchOptions?: (searchedText: string) => Promise<T[] | undefined>;
  label: string;
  error?: Record<string, any>;
  value?: T[];
  onChange?: (value: T[]) => void;
}
const useStyles = makeStyles(() => ({
  autoCompleteInput: {
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'none'
    },
    '& .MuiInputLabel-formControl': {
      position: 'static',
      color: INPUT_LABEL_COLOR,
      fontSize: '1.1rem',
      marginBottom: '0.825rem'
    },
    '& .MuiOutlinedInput-notchedOutline legend': {
      display: 'none'
    },
    '& .MuiInputBase-input': {
      marginTop: '-0.5rem'
    },
    '& .MuiFormControl-root': {
      paddingBottom: 0,
      marginBottom: 0
    },
    '& .MuiFormHelperText-root': {
      marginLeft: 0
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: INPUT_BORDER_COLOR
    },
    '& .MuiChip-labelSmall': {
      maxWidth: '45rem'
    }
  }
}));
const MultipleAutocompleteInput = <T,>(
  props: IMultipleAutocompleteInputProps<T>
): JSX.Element => {
  const {
    fetchOptions,
    label,
    placeholder,
    error,
    className,
    options: optionsList,
    onChange,
    value = [] as T[]
  } = props;
  const classes = useStyles();
  const [options, setOptions] = React.useState<T[]>(
    Array.isArray(optionsList) ? optionsList : [] // Ensure options is always an array
  );
  const [enteredValue, setEnteredValue] = React.useState('');
  const [loading, setLoading] = useState(false);
  const {enqueueSnackbar} = useSnackbar();
  const updateOptions = async () => {
    if (!fetchOptions) return;
    try {
      setLoading(true);
      const filteredOptions = await fetchOptions(enteredValue);
      if (filteredOptions && Array.isArray(filteredOptions)) {
        // Exclude selected values from the options list
        const optionsWithoutSelected = filteredOptions.filter(
          (option: T) =>
            !value.some(
              (selectedValue: T) =>
                (selectedValue as any).label === (option as any).label
            )
        );
        setOptions(optionsWithoutSelected);
      }
    } catch (err: any) {
      enqueueSnackbar(err?.message, SnackBarConfig('e'));
    } finally {
      setLoading(false);
    }
  };
  const {t} = useTranslation();

  const debouncedFetch = useDebouncedCallback(
    () => updateOptions(),
    SEARCH_DEBOUNCE_DELAY
  );

  React.useEffect(() => {
    if (!fetchOptions) return;
    debouncedFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enteredValue, debouncedFetch]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // eslint-disable-next-line no-shadow
  const handleInputChange = (value: any) => {
    setEnteredValue(value);
  };
  const handleSelectChange = (event: any, newValue: any) => {
    if (onChange) {
      onChange(newValue);
    }
  };
  return (
    <Autocomplete
      multiple
      fullWidth
      id="tags-standard"
      size="small"
      loading={loading}
      options={options}
      value={value}
      className={`${classes.autoCompleteInput} ${className}`}
      getOptionLabel={(option: T) => (option as any).label}
      onChange={handleSelectChange} // Handle selection change
      onInputChange={(e, newValue, reason) => {
        handleInputChange(newValue);
      }}
      renderOption={(option, {selected}) => (
        <React.Fragment>
          <Checkbox style={{marginRight: 8}} checked={selected} />
          {(option as any).label}
        </React.Fragment>
      )}
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          label={label}
          placeholder={placeholder}
          error={!!error}
          helperText={error?.message ? t(error.message) : ''}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            )
          }}
          InputLabelProps={{
            shrink: true
          }}
        />
      )}
    />
  );
};

export default MultipleAutocompleteInput;
